import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Spinner, Alert } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const EditInvoice = () => {
  const [formData, setFormData] = useState({
    customer_id: "",
    date: new Date().toISOString().split('T')[0],
    gst_percentage: 18,
    packageCharges: 0,
    notes: "",
    proforma_id: "",
    quotation_id: "",
    status: "Generated",
    productSearch: "",
    sellingRate: "",
    qty: "",
    selectedProduct: null
  });

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [submitError, setSubmitError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [selectedCustomerAddress, setSelectedCustomerAddress] = useState("");
  const [fetchingProforma, setFetchingProforma] = useState(false);
  const [fetchingQuotation, setFetchingQuotation] = useState(false);

  // Customer modal states
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    mobile: "",
    address: "",
    gst_no: "",
    state: "",
    city: "",
    pin_code: ""
  });
  const [customerErrors, setCustomerErrors] = useState({});
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [customerSuccess, setCustomerSuccess] = useState("");

  // Refs for input fields
  const customerRef = useRef(null);
  const dateRef = useRef(null);
  const productSearchRef = useRef(null);
  const sellingRateRef = useRef(null);
  const qtyRef = useRef(null);
  const notesRef = useRef(null);

  const navigate = useNavigate();
  const { id } = useParams();

  // Fetch data on component mount
  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    try {
      setLoadingData(true);
      
      // Fetch customers
      const customersResponse = await api.post("/customer_list.php", {});
      if (customersResponse.data.head.code === 200) {
        setCustomers(customersResponse.data.body);
      }

      // Fetch products
      const productsResponse = await api.post("/product_list.php", {});
      if (productsResponse.data.head.code === 200) {
        setProducts(productsResponse.data.body);
      }

      // Fetch proformas
      const proformasResponse = await api.post("/proforma_list.php", {});
      if (proformasResponse.data.head.code === 200) {
        setProformas(proformasResponse.data.body);
      }

      // Fetch quotations
      const quotationsResponse = await api.post("/quotation_list.php", {});
      if (quotationsResponse.data.head.code === 200) {
        setQuotations(quotationsResponse.data.body);
      }

      // Fetch invoice data
      if (id) {
        const invoiceResponse = await api.post("/invoice_detail.php", { id });
        if (invoiceResponse.data.head.code === 200) {
          const invoice = invoiceResponse.data.body;
          
          setFormData({
            customer_id: invoice.customer_id || "",
            date: invoice.date || new Date().toISOString().split('T')[0],
            gst_percentage: invoice.gst_percentage || 18,
            packageCharges: invoice.package_charges || 0,
            notes: invoice.notes || "",
            proforma_id: invoice.proforma_id || "",
            quotation_id: invoice.quotation_id || "",
            status: invoice.status || "Generated",
            productSearch: "",
            sellingRate: "",
            qty: "",
            selectedProduct: null
          });
          
          // Set customer address if customer exists
          if (invoice.customer_id) {
            const selectedCustomer = customers.find(c => c.id == invoice.customer_id);
            if (selectedCustomer) {
              const addressParts = [];
              if (selectedCustomer.address) addressParts.push(selectedCustomer.address);
              if (selectedCustomer.city) addressParts.push(selectedCustomer.city);
              if (selectedCustomer.state) addressParts.push(selectedCustomer.state);
              if (selectedCustomer.pin_code) addressParts.push(selectedCustomer.pin_code);
              setSelectedCustomerAddress(addressParts.join(", "));
            }
          }
          
          // Transform and set items
          const items = (invoice.items || []).map(item => ({
            product_code: item.product_code || '',
            product_name: item.product_name || item.item_name || '',
            selling_rate: parseFloat(item.selling_rate) || 0,
            quantity: parseInt(item.quantity) || 1,
            total: parseFloat(item.total) || 0
          }));
          
          setInvoiceItems(items);
        } else {
          setSubmitError("Failed to load invoice data");
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setSubmitError("Failed to load data");
    } finally {
      setLoadingData(false);
    }
  };

  // Function to fetch proforma details
  const fetchProformaDetails = async (proformaId) => {
    try {
      setFetchingProforma(true);
      const response = await api.post("/proforma_detail.php", { id: proformaId });
      
      if (response.data.head.code === 200) {
        const data = response.data.body;
        
        // Set customer
        setFormData(prev => ({ 
          ...prev, 
          customer_id: data.customer_id,
          proforma_id: proformaId
        }));
        
        // Set customer address
        const selectedCustomer = customers.find(c => c.id == data.customer_id);
        if (selectedCustomer) {
          const addressParts = [];
          if (selectedCustomer.address) addressParts.push(selectedCustomer.address);
          if (selectedCustomer.city) addressParts.push(selectedCustomer.city);
          if (selectedCustomer.state) addressParts.push(selectedCustomer.state);
          if (selectedCustomer.pin_code) addressParts.push(selectedCustomer.pin_code);
          setSelectedCustomerAddress(addressParts.join(", "));
        }
        
        // Set GST percentage and notes
        setFormData(prev => ({ 
          ...prev, 
          gst_percentage: data.gst_percentage || 18,
          notes: data.notes || ""
        }));
        
        // Transform and set items from proforma
        const items = (data.items || []).map(item => ({
          product_code: item.product_code || '',
          product_name: item.product_name || item.item_name || '',
          selling_rate: parseFloat(item.selling_rate) || 0,
          quantity: parseInt(item.quantity) || 1,
          total: parseFloat(item.total) || 0
        }));
        
        setInvoiceItems(items);
        
        // Show success message
        alert(`Proforma #${proformaId} loaded successfully with ${items.length} items.`);
      } else {
        alert("Failed to fetch proforma details. Please check the proforma number.");
      }
    } catch (err) {
      console.error("Error fetching proforma:", err);
      alert("Failed to fetch proforma details.");
    } finally {
      setFetchingProforma(false);
    }
  };

  // Function to fetch quotation details
  const fetchQuotationDetails = async (quotationId) => {
    try {
      setFetchingQuotation(true);
      const response = await api.post("/quotation_detail.php", { id: quotationId });
      
      if (response.data.head.code === 200) {
        const data = response.data.body;
        
        // Set customer
        setFormData(prev => ({ 
          ...prev, 
          customer_id: data.customer_id,
          quotation_id: quotationId
        }));
        
        // Set customer address
        const selectedCustomer = customers.find(c => c.id == data.customer_id);
        if (selectedCustomer) {
          const addressParts = [];
          if (selectedCustomer.address) addressParts.push(selectedCustomer.address);
          if (selectedCustomer.city) addressParts.push(selectedCustomer.city);
          if (selectedCustomer.state) addressParts.push(selectedCustomer.state);
          if (selectedCustomer.pin_code) addressParts.push(selectedCustomer.pin_code);
          setSelectedCustomerAddress(addressParts.join(", "));
        }
        
        // Set GST percentage
        setFormData(prev => ({ 
          ...prev, 
          gst_percentage: data.gst_percentage || 18 
        }));
        
        // Transform and set items from quotation
        const items = (data.items || []).map(item => ({
          product_code: item.product_code || '',
          product_name: item.item_name || '',
          selling_rate: parseFloat(item.selling_rate) || 0,
          quantity: parseInt(item.quantity) || 1,
          total: parseFloat(item.total) || 0
        }));
        
        setInvoiceItems(items);
        
        // Show success message
        alert(`Quotation #${quotationId} loaded successfully with ${items.length} items.`);
      } else {
        alert("Failed to fetch quotation details. Please check the quotation number.");
      }
    } catch (err) {
      console.error("Error fetching quotation:", err);
      alert("Failed to fetch quotation details.");
    } finally {
      setFetchingQuotation(false);
    }
  };

  // Handle proforma input change with auto-fetch
  const handleProformaChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, proforma_id: value }));
    
    // Auto-fetch if it looks like a complete proforma number
    if (value.trim().length >= 3 && !isNaN(value)) {
      await fetchProformaDetails(value);
    }
  };

  // Handle quotation input change with auto-fetch
  const handleQuotationChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, quotation_id: value }));
    
    // Auto-fetch if it looks like a complete quotation number
    if (value.trim().length >= 3 && !isNaN(value)) {
      await fetchQuotationDetails(value);
    }
  };

  // Filter products based on search input
  const getFilteredProducts = () => {
    if (!formData.productSearch) {
      return products.slice(0, 10);
    }
    
    const searchTerm = formData.productSearch.toLowerCase();
    const filtered = products.filter(product => {
      const hsnCode = product.hsn_code || product.code || "";
      const productName = product.name || product.product_name || "";
      return (
        hsnCode.toLowerCase().includes(searchTerm) ||
        productName.toLowerCase().includes(searchTerm)
      );
    });
    return filtered.slice(0, 10);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'customer_id') {
      // Find selected customer to get their address
      const selectedCustomer = customers.find(c => c.id == value);
      if (selectedCustomer) {
        // Build the address string
        const addressParts = [];
        if (selectedCustomer.address) addressParts.push(selectedCustomer.address);
        if (selectedCustomer.city) addressParts.push(selectedCustomer.city);
        if (selectedCustomer.state) addressParts.push(selectedCustomer.state);
        if (selectedCustomer.pin_code) addressParts.push(selectedCustomer.pin_code);
        
        setSelectedCustomerAddress(addressParts.join(", "));
      } else {
        setSelectedCustomerAddress("");
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'productSearch') {
      setShowProductDropdown(true);
    } else if (name === 'proforma_id') {
      handleProformaChange(e);
    } else if (name === 'quotation_id') {
      handleQuotationChange(e);
    }
  };

  // Handle product selection from dropdown
  const handleProductSelect = (product) => {
    const productName = product.name || product.product_name || "";
    const hsnCode = product.hsn_code || product.code || "";
    
    setFormData(prev => ({
      ...prev,
      productSearch: `${hsnCode} - ${productName}`,
      selectedProduct: {
        hsn_code: hsnCode,
        name: productName,
        id: product.id
      }
    }));
    setShowProductDropdown(false);
    sellingRateRef.current.focus();
  };

  const handleAddItem = () => {
    if (!formData.selectedProduct || !formData.sellingRate || !formData.qty) {
      alert("Please fill all product fields");
      return;
    }

    const newItem = {
      product_code: formData.selectedProduct.hsn_code || '',
      product_name: formData.selectedProduct.name,
      selling_rate: parseFloat(formData.sellingRate) || 0,
      quantity: parseInt(formData.qty) || 1,
      total: (parseFloat(formData.sellingRate) || 0) * (parseInt(formData.qty) || 1)
    };

    setInvoiceItems([...invoiceItems, newItem]);
    
    // Reset product fields only
    setFormData(prev => ({ 
      ...prev, 
      productSearch: "",
      selectedProduct: null,
      sellingRate: "",
      qty: ""
    }));
    
    // Focus back on product search for next entry
    productSearchRef.current.focus();
    setShowProductDropdown(false);
  };

  // Enter key navigation handler
  const handleKeyPress = (e, nextFieldRef, action = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (action) {
        action();
      } else if (nextFieldRef && nextFieldRef.current) {
        nextFieldRef.current.focus();
      }
    }
  };

  // Special handler for product search
  const handleProductSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filtered = getFilteredProducts();
      if (filtered.length === 1) {
        handleProductSelect(filtered[0]);
      } else if (filtered.length > 0 && formData.productSearch) {
        sellingRateRef.current.focus();
      }
    }
  };

  // Special handler for selling rate field
  const handleSellingRateKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (formData.selectedProduct && formData.sellingRate) {
        qtyRef.current.focus();
      }
    }
  };

  // Special handler for quantity field to add item
  const handleQtyKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  const handleGstChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, gst_percentage: value }));
  };

  const handlePackageChargesChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, packageCharges: parseFloat(value) || 0 }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceItems];
    
    if (field === 'selling_rate') {
      updatedItems[index].selling_rate = parseFloat(value) || 0;
    } else if (field === 'quantity') {
      updatedItems[index].quantity = parseInt(value) || 0;
    }
    
    // Calculate total if selling rate or quantity changes
    if (field === 'selling_rate' || field === 'quantity') {
      updatedItems[index].total = updatedItems[index].selling_rate * updatedItems[index].quantity;
    }
    
    setInvoiceItems(updatedItems);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(updatedItems);
  };

  // Customer Modal Functions
  const handleShowCustomerModal = () => {
    setShowCustomerModal(true);
    setCustomerErrors({});
    setCustomerSuccess("");
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerModal(false);
    // Reset form
    setNewCustomer({
      name: "",
      mobile: "",
      address: "",
      gst_no: "",
      state: "",
      city: "",
      pin_code: ""
    });
    setCustomerErrors({});
    setCustomerSuccess("");
    setIsAddingCustomer(false);
  };

  // Handle new customer form change
  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (customerErrors[name]) {
      setCustomerErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Validate customer form
  const validateCustomer = () => {
    const errors = {};
    
    if (!newCustomer.name.trim()) {
      errors.name = "Customer name is required";
    }
    
    if (!newCustomer.mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(newCustomer.mobile)) {
      errors.mobile = "Enter a valid 10-digit mobile number";
    }
    
    return errors;
  };

  // Handle adding customer via API
  const handleAddCustomer = async () => {
    const validationErrors = validateCustomer();
    
    if (Object.keys(validationErrors).length > 0) {
      setCustomerErrors(validationErrors);
      return;
    }

    try {
      setIsAddingCustomer(true);
      setCustomerSuccess("");
      setCustomerErrors({});

      // Prepare payload matching customer_add.php API
      const payload = {
        name: newCustomer.name,
        mobile: newCustomer.mobile,
        gst_no: newCustomer.gst_no || "",
        state: newCustomer.state || "",
        city: newCustomer.city || "",
        address: newCustomer.address || "",
        pin_code: newCustomer.pin_code || ""
      };

      // Call the customer_add.php API
      const response = await api.post("/customer_add.php", payload);

      if (response.data.head.code === 200) {
        // Success - add new customer to local state
        const newCustomerObj = {
          id: response.data.body.customer_id,
          name: newCustomer.name,
          mobile: newCustomer.mobile,
          gst_no: newCustomer.gst_no,
          state: newCustomer.state,
          city: newCustomer.city,
          address: newCustomer.address,
          pin_code: newCustomer.pin_code
        };

        // Add to customers list
        setCustomers(prev => [...prev, newCustomerObj]);
        
        // Auto-select the new customer
        setFormData(prev => ({ 
          ...prev, 
          customer_id: response.data.body.customer_id 
        }));

        // Set the address for the new customer
        const addressParts = [];
        if (newCustomer.address) addressParts.push(newCustomer.address);
        if (newCustomer.city) addressParts.push(newCustomer.city);
        if (newCustomer.state) addressParts.push(newCustomer.state);
        if (newCustomer.pin_code) addressParts.push(newCustomer.pin_code);
        setSelectedCustomerAddress(addressParts.join(", "));

        // Show success message
        setCustomerSuccess(`Customer "${newCustomer.name}" added successfully!`);
        
        // Reset form and close modal after 2 seconds
        setTimeout(() => {
          handleCloseCustomerModal();
          // Focus back on customer dropdown
          if (customerRef.current) {
            customerRef.current.focus();
          }
        }, 2000);

      } else {
        // Handle API error
        const errorMsg = response.data.head.msg || "Failed to add customer";
        setCustomerErrors({ api: errorMsg });
      }
    } catch (err) {
      console.error("Error adding customer:", err);
      
      let errorMessage = "Failed to add customer. Please try again.";
      
      if (err.response) {
        errorMessage = err.response.data?.head?.msg || errorMessage;
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      }
      
      setCustomerErrors({ api: errorMessage });
    } finally {
      setIsAddingCustomer(false);
    }
  };

  // Handle keydown in customer modal
  const handleCustomerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddCustomer();
    }
  };

  const calculateSubTotal = () => {
    return invoiceItems.reduce((total, item) => total + (item.total || 0), 0);
  };

  const calculateGST = () => {
    const subTotal = calculateSubTotal();
    return (subTotal * (parseFloat(formData.gst_percentage) / 100));
  };

  const calculatePackageCharges = () => {
    return parseFloat(formData.packageCharges) || 0;
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const gst = calculateGST();
    const packageCharges = calculatePackageCharges();
    return subTotal + gst + packageCharges;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_id) newErrors.customer_id = "Customer is required";
    if (!formData.date) newErrors.date = "Date is required";
    if (invoiceItems.length === 0) newErrors.items = "At least one item is required";
    return newErrors;
  };

  const generateInvoicePayload = () => {
    const subTotal = calculateSubTotal();
    const gstAmount = calculateGST();
    const packageCharges = calculatePackageCharges();
    const grandTotal = calculateGrandTotal();
    
    return {
      id: parseInt(id),
      customer_id: parseInt(formData.customer_id) || 0,
      date: formData.date,
      proforma_id: formData.proforma_id ? parseInt(formData.proforma_id) : "",
      quotation_id: formData.quotation_id ? parseInt(formData.quotation_id) : "",
      sub_total: subTotal.toFixed(2),
      gst_percentage: parseFloat(formData.gst_percentage),
      gst_amount: gstAmount.toFixed(2),
      packageCharges: packageCharges.toFixed(2),
      grand_total: grandTotal.toFixed(2),
      status: formData.status,
      notes: formData.notes || "",
      items: invoiceItems.map(item => ({
        product_code: item.product_code.toString(),
        product_name: item.product_name.toString(),
        selling_rate: parseFloat(item.selling_rate),
        quantity: parseInt(item.quantity),
        total: parseFloat(item.total)
      }))
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setLoading(true);
      setSubmitError("");

      const payload = generateInvoicePayload();
      
      const response = await api.post("/invoice_edit.php", payload);
      
      if (response.data.head.code === 200) {
        alert(`Invoice #${id} updated successfully!`);
        navigate("/invoice-list");
      } else {
        const errorMsg = response.data.head.msg || "Failed to update invoice";
        setSubmitError(errorMsg);
      }
    } catch (err) {
      console.error("Error updating invoice:", err);
      setSubmitError("Failed to update invoice. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderError = (field) =>
    errors[field] ? <div className="text-danger small mt-1">{errors[field]}</div> : null;

  // Render customer field error
  const renderCustomerError = (field) =>
    customerErrors[field] ? (
      <div className="text-danger small mt-1">{customerErrors[field]}</div>
    ) : null;

  if (loadingData) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Invoice #{id}</h4>
          <Button className="btn-top" onClick={() => navigate("/invoice")}>Back</Button>
        </div>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading invoice data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Edit Invoice #{id}</h4>
        <Button className="btn-top" onClick={() => navigate("/invoice")} disabled={loading}>
          Back
        </Button>
      </div>

      {submitError && (
        <Alert variant="danger" className="mb-3">
          {submitError}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          {/* Customer and Address Row */}
          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Select Customer</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  ref={customerRef}
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, dateRef)}
                  disabled={loading}
                  isInvalid={!!errors.customer_id}
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </Form.Select>
                <Button 
                  variant="outline-primary" 
                  onClick={handleShowCustomerModal}
                  className="d-flex align-items-center"
                  title="Add New Customer"
                  disabled={loading}
                  type="button"
                >
                  <FaPlus />
                </Button>
              </div>
              {renderError("customer_id")}
            </Form.Group>
          </Col>

          <Col lg={5} md={5} sm={12}>
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                value={selectedCustomerAddress}
                readOnly
                placeholder="Address will appear after selecting customer"
                className="address-field"
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={3} sm={12}>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                ref={dateRef}
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                disabled={loading}
                isInvalid={!!errors.date}
              />
              {renderError("date")}
            </Form.Group>
          </Col>

          {/* Status Field */}
          <Col lg={3} md={3} sm={12}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="Generated">Generated</option>
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
                <option value="Overdue">Overdue</option>
                <option value="Cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Proforma ID Field */}
          <Col lg={3} md={3} sm={12}>
            <Form.Group>
              <Form.Label>Proforma Number (Optional)</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control
                  name="proforma_id"
                  type="text"
                  value={formData.proforma_id}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, productSearchRef)}
                  placeholder="Enter proforma number"
                  disabled={loading || fetchingProforma}
                />
                {fetchingProforma && (
                  <Spinner animation="border" size="sm" className="ms-2" />
                )}
              </div>
            </Form.Group>
          </Col>

          {/* Quotation ID Field */}
          <Col lg={3} md={3} sm={12}>
            <Form.Group>
              <Form.Label>Quotation Number (Optional)</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control
                  name="quotation_id"
                  type="text"
                  value={formData.quotation_id}
                  onChange={handleChange}
                  placeholder="Enter quotation number"
                  disabled={loading || fetchingQuotation}
                />
                {fetchingQuotation && (
                  <Spinner animation="border" size="sm" className="ms-2" />
                )}
              </div>
            </Form.Group>
          </Col>

          {/* Product Search Row */}
          <Col lg={6} md={6} sm={12}>
            <Form.Group className="position-relative">
              <Form.Label>Search Product *</Form.Label>
              <Form.Control
                ref={productSearchRef}
                name="productSearch"
                type="text"
                value={formData.productSearch}
                onChange={handleChange}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                onKeyPress={handleProductSearchKeyPress}
                placeholder="Type Product Code or Name"
                autoComplete="off"
                disabled={loading}
              />
              {showProductDropdown && (
                <div className="custom-dropdown">
                  {getFilteredProducts().map((product) => {
                    const productName = product.name || product.product_name || "";
                    const productCode = product.id || "";
                    return (
                      <div
                        key={product.id}
                        className="dropdown-item"
                        onClick={() => handleProductSelect(product)}
                      >
                        <div className="fw-bold">{productCode} - {productName}</div>
                      </div>
                    );
                  })}
                  {getFilteredProducts().length === 0 && (
                    <div className="dropdown-item text-muted">
                      No products found
                    </div>
                  )}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col lg={2} md={2} sm={6}>
            <Form.Group>
              <Form.Label>Selling Rate (₹) *</Form.Label>
              <Form.Control
                ref={sellingRateRef}
                className="right-align"
                name="sellingRate"
                type="number"
                value={formData.sellingRate}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="0.01"
                onKeyPress={handleSellingRateKeyPress}
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col lg={2} md={2} sm={6}>
            <Form.Group>
              <Form.Label>Quantity *</Form.Label>
              <Form.Control
                ref={qtyRef}
                className="right-align"
                name="qty"
                type="number"
                value={formData.qty}
                onChange={handleChange}
                placeholder="0"
                min="1"
                onKeyPress={handleQtyKeyPress}
                disabled={loading}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Invoice Items Table */}
        <Row className="mt-4">
          <Col xs={12}>
            <div className="table-responsive">
              <Table bordered className="purchase-table">
                <thead className="table-dark">
                  <tr>
                    <th width="5%">Sno</th>
                    <th width="25%">Product Name</th>
                    <th width="15%">HSN Code</th>
                    <th width="15%">Selling Rate (₹)</th>
                    <th width="10%">Qty (Kgs)</th>
                    <th width="15%">Total (₹)</th>
                    <th width="10%">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceItems.map((item, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>{item.product_name}</td>
                      <td className="text-center">{item.product_code}</td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.selling_rate}
                          className="right-align"
                          onChange={(e) => handleItemChange(index, 'selling_rate', e.target.value)}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          disabled={loading}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          placeholder="0"
                          min="1"
                          className="right-align"
                          disabled={loading}
                        />
                      </td>
                      <td className="text-center">₹{item.total.toFixed(2)}</td>
                      <td className="text-center">
                        <FaTrash
                          className="delete-icon"
                          style={{ cursor: loading ? 'not-allowed' : 'pointer', color: '#dc3545', opacity: loading ? 0.5 : 1 }}
                          onClick={() => !loading && handleDeleteItem(index)}
                          title="Delete Item"
                        />
                      </td>
                    </tr>
                  ))}
                  {invoiceItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-3">
                        No items added yet. Enter proforma/quotation number to auto-fill or manually add items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {renderError("items")}

        {/* Totals Section with Notes */}
        <Row className="mt-4">
          <Col lg={8} md={8} sm={12}>
            <Form.Group>
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                ref={notesRef}
                name="notes"
                as="textarea"
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                placeholder="Enter any notes..."
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col lg={4} md={4} sm={12} className="d-flex justify-content-end">
            <div className="totals-section">
              <div className="total-row">
                <span className="total-label">Subtotal:</span>
                <span className="total-value">₹{calculateSubTotal().toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span className="total-label">
                  GST (
                  <Form.Control
                    type="number"
                    name="gst_percentage"
                    value={formData.gst_percentage}
                    onChange={handleGstChange}
                    className="gst-input"
                    min="0"
                    max="100"
                    step="0.1"
                    disabled={loading}
                  />
                  %)
                </span>
                <span className="total-value">₹{calculateGST().toFixed(2)}</span>
              </div>
              {/* <div className="total-row">
                <span className="total-label">Package Charges:</span>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="number"
                    name="packageCharges"
                    value={formData.packageCharges}
                    onChange={handlePackageChargesChange}
                    className="package-input"
                    min="0"
                    step="0.01"
                    disabled={loading}
                  />
                  <span className="total-value ms-2">₹{calculatePackageCharges().toFixed(2)}</span>
                </div>
              </div> */}
              <div className="total-row grand-total">
                <span className="total-label">Grand Total:</span>
                <span className="total-value">₹{calculateGrandTotal().toFixed(2)}</span>
              </div>
            </div>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button 
            type="submit" 
            className="btn-submit"
            disabled={loading || invoiceItems.length === 0}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Invoice"
            )}
          </Button>
          <Button 
            type="button" 
            className="btn-top" 
            onClick={() => navigate("/invoice-list")}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </Form>

      {/* Add Customer Modal */}
      <Modal show={showCustomerModal} onHide={handleCloseCustomerModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customerSuccess && (
            <Alert variant="success" className="mb-3">
              {customerSuccess}
            </Alert>
          )}

          {customerErrors.api && (
            <Alert variant="danger" className="mb-3">
              {customerErrors.api}
            </Alert>
          )}

          <Form onKeyDown={handleCustomerKeyDown}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={newCustomer.name}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter customer name"
                    required
                    isInvalid={!!customerErrors.name}
                    autoFocus
                  />
                  {renderCustomerError("name")}
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={newCustomer.mobile}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    required
                    isInvalid={!!customerErrors.mobile}
                  />
                  {renderCustomerError("mobile")}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={newCustomer.address}
                onChange={handleNewCustomerChange}
                placeholder="Enter address"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={newCustomer.city}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={newCustomer.state}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter state"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pin Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="pin_code"
                    value={newCustomer.pin_code}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter pin code"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Number (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="gst_no"
                    value={newCustomer.gst_no}
                    onChange={handleNewCustomerChange}
                    placeholder="Enter GST number"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleCloseCustomerModal}
            disabled={isAddingCustomer}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddCustomer} 
            disabled={!newCustomer.name.trim() || !newCustomer.mobile.trim() || isAddingCustomer}
          >
            {isAddingCustomer ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add Customer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EditInvoice;